import moment from 'moment';
import {
  generatePdfFromTemplate,
  PdfTemplateLayoutItem,
  GeneratePdfTemplateResult,
  PdfTemplateFontWeight,
} from './PdfTemplateService';

const IMPROVEMENT_PDF_URL =
  'https://arb-systems-s3-public.s3.amazonaws.com/public/Performance/PerformanceManagement_Improvement.pdf';

const formatDateValue = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.format('MM/DD/YYYY');
};

const extractField = (data: any, candidates: string[]) => {
  for (const key of candidates) {
    const segments = key.split('.');
    let current = data;

    for (const segment of segments) {
      if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
        current = current[segment];
      } else {
        current = undefined;
        break;
      }
    }

    if (current !== undefined && current !== null) {
      if (typeof current === 'string') {
        const trimmed = current.trim();
        if (trimmed !== '') {
          return trimmed;
        }
      }

      if (typeof current === 'number') {
        return current.toString();
      }

      if (current instanceof Date) {
        return moment(current).format('MM/DD/YYYY');
      }
    }
  }

  return '';
};

const getPolicyViolationText = (item: any): string => {
  if (item?.policyViolations && Array.isArray(item.policyViolations) && item.policyViolations.length > 0) {
    return item.policyViolations
      .map((pv: any) => (typeof pv === 'string' ? pv : pv?.name ?? ''))
      .filter(Boolean)
      .join(', ');
  }
  return extractField(item, ['policy_violation', 'policyViolation', 'policyViolation.name', 'policy_violated']) || '';
};

const sanitizeFileName = (text: string) => {
  if (!text) {
    return 'performance-improvement';
  }

  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 80) || 'performance-improvement'
  );
};

interface AddTextOptions {
  y?: number;
  x?: number;
  textColor?: string;
  page?: number;
  fontSize?: number;
  fontWeight?: PdfTemplateFontWeight;
  maxWidth?: number;
  lineHeight?: number;
  spacingAfter?: number;
}

const buildImprovementLayout = (item: any, userLogged?: any, currentSchool?: any) => {
  const employeeName = extractField(item, ['name', 'employeeName', 'employee_name', 'employee.name']) || '';
  const position = extractField(item, ['position', 'employeePosition', 'jobTitle', 'job_title']) || '';
  const supervisor = extractField(item, ['supervisor', 'supervisor.name', 'supervisorName', 'supervisor_name']) || '';
  const department = extractField(item, ['department', 'department.name', 'departmentName', 'department_name']);
  const policyViolation = getPolicyViolationText(item);
  const dateOfIncident = formatDateValue(extractField(item, ['date_of_incident', 'dateOfIncident', 'date']));
  const reasonForPlan = extractField(item, [
    'reason_for_performance_improvement_plan',
    'reasonForPerformanceImprovementPlan',
    'reason',
  ]);
  const expectationsAndGoals = extractField(item, ['expectations_and_goals', 'expectationsAndGoals', 'expectations']);
  const timelineAndDeadline = extractField(item, ['timeline_and_deadline', 'timelineAndDeadline', 'timeline']);
  const resourcesAndSupport = extractField(item, ['resources_and_support', 'resourcesAndSupport', 'resources']);
  const evaluationDate = formatDateValue(
    extractField(item, ['evaluation_date', 'evaluationDate', 'review_date', 'reviewDate']),
  );
  const createdAt = formatDateValue(extractField(item, ['created_at', 'createdAt']));

  const layout: PdfTemplateLayoutItem[] = [];
  const addText = (text: string, options: AddTextOptions = {}) => {
    const { x, ...rest } = options;
    const textItem: PdfTemplateLayoutItem = {
      type: 'text',
      text,
      x: x ?? 54,
      ...rest,
    };
    layout.push(textItem);
  };

  addText(`${currentSchool?.label ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 16,
    textColor: '#4d87e6',
    y: 711,
    x: 203,
  });

  const submittedByName = item?.submittedByUser
    ? `${item.submittedByUser.firstName ?? ''} ${item.submittedByUser.lastName ?? ''}`.trim()
    : '';

  addText(submittedByName, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 623,
    x: 152,
  });

  addText(`${employeeName}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 586,
    x: 110,
  });

  addText(`${dateOfIncident}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 586,
    x: 400,
  });

  addText(`${position}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 562,
    x: 120,
  });

  addText(`${department ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 562,
    x: 355,
  });

  const sequentialX = 80;
  const sequentialFontSize = 12;
  const spacingAfterTitle = 10;
  const spacingAfterContent = 22;

  addText('Policy Violation:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${policyViolation ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Reason for Performance Improvement Plan:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${reasonForPlan ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Expectations and Goals:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${expectationsAndGoals ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Timeline and/or Deadline:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${timelineAndDeadline ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Resources and Support:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${resourcesAndSupport ?? ''}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  return {
    layout,
    employeeName,
    helpers: {
      addText,
      extractField,
      formatDateValue,
    },
    context: {
      item,
      userLogged,
      currentSchool,
      position,
      supervisor,
      department,
      policyViolation,
      dateOfIncident,
      reasonForPlan,
      evaluationDate,
      createdAt,
    },
  };
};

export interface ImprovementPdfResult extends GeneratePdfTemplateResult {
  fileName: string;
}

export const generateImprovementPdf = async (
  item: any,
  userLogged?: any,
  currentSchool?: any,
): Promise<ImprovementPdfResult> => {
  const { layout, employeeName } = buildImprovementLayout(item, userLogged, currentSchool);
  const result = await generatePdfFromTemplate({
    templatePath: IMPROVEMENT_PDF_URL,
    layout,
    cursorStart: 730,
    templateLastPageIndex: 1,
  });

  return {
    ...result,
    fileName: `${sanitizeFileName(employeeName)}-performance-improvement.pdf`,
  };
};
